/**
 * Endless product grid in the search drawer's empty state.
 *
 * The first page is server-rendered (snippets/predictive-search-empty-state.liquid).
 * A sentinel under the grid carries the next page number; when it scrolls near
 * the bottom of the drawer's scroll pane, the next page of the same section is
 * fetched and its cards appended. The sentinel is removed once the server stops
 * emitting one, which is the last page.
 */

import { announceHydrated } from '@theme/pag-product-hydrate';

const SECTION_ID = 'predictive-search-empty';
const MORE = '[data-pag-search-more]';
const GRID = '[data-pag-search-grid]';

/** @type {IntersectionObserver | null} */
let observer = null;

/** @param {HTMLElement} more */
async function loadNext(more) {
  if (more.dataset.loading) return;
  more.dataset.loading = 'true';

  const grid = more.closest('.pag-search-browse')?.querySelector(GRID);
  if (!grid) return;

  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('section_id', SECTION_ID);
  url.searchParams.set('page', more.dataset.nextPage ?? '2');

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

    const cards = [...doc.querySelectorAll(`${GRID} > *`)];
    grid.append(...cards.map((card) => document.importNode(card, true)));
    announceHydrated(grid);

    const next = doc.querySelector(MORE);
    if (next instanceof HTMLElement) {
      more.dataset.nextPage = next.dataset.nextPage ?? '';
      delete more.dataset.loading;
      // Still on screen after the append? Observe again so the next page loads.
      observer?.unobserve(more);
      observer?.observe(more);
    } else {
      observer?.unobserve(more);
      more.remove();
    }
  } catch {
    delete more.dataset.loading;
  }
}

function observe() {
  const more = document.querySelector(MORE);
  if (!(more instanceof HTMLElement) || more.dataset.observed) return;

  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadNext(more);
    },
    { root: more.closest('.predictive-search-form__content'), rootMargin: '0px 0px 600px 0px' }
  );
  more.dataset.observed = 'true';
  observer.observe(more);
}

// predictive-search.js dispatches this after it morphs the empty state back in.
document.addEventListener('shopify:section:load', observe);
observe();
